/**
 * Basemap styling from the ShiftWagon rebrand asset drop.
 *
 * Google draws the basemap in its own renderer, so this is the only way to stop
 * the map being the one surface that ignores the palette — a default Google map
 * under a teal route and asphalt pins reads as a third-party widget dropped into
 * the page, which on the tracking screen is the thing the customer stares at.
 *
 * The brief's two sets, transcribed from `map-styles/*.json`. Both quiet the
 * basemap in the same way: no POI, no transit, no label icons, land and water
 * pulled toward the brand neutrals, and every road reduced to a fill and a
 * hairline stroke. The route line and the markers are the only saturated things
 * left, which is the point.
 */

/**
 * Declared structurally rather than as `google.maps.MapTypeStyle[]`.
 *
 * The project has no `@types/google.maps` — the Maps library is loaded at
 * runtime from a script tag and typed `any` at its call sites. Naming the
 * global namespace here would be the one reference to types nothing installs,
 * so the build would break on a clean checkout. This shape is small, stable,
 * and catches the mistake that actually happens: a typo'd `featureType` or a
 * styler key Google silently ignores.
 */
interface MapTypeStyle {
  featureType?: string;
  elementType?: string;
  stylers: Array<{ color?: string; visibility?: string }>;
}

type MapStyle = MapTypeStyle[];

/**
 * Paper. The style the product ships on.
 *
 * Note the hierarchy it keeps: arterials and highways are pure white against a
 * `#FAF9F6` road fill and `#F2F0EA` land, so the road network still reads as a
 * network at a glance without any of it competing with the teal route drawn on
 * top. Water labels are teal because water is the one basemap feature allowed
 * to carry brand colour — it never sits under the route.
 */
export const LIGHT_MAP_STYLE: MapStyle = [
  { elementType: 'geometry', stylers: [{ color: '#F2F0EA' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#565D64' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#FAF9F6' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#D8D4C9' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#F2F0EA' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#FAF9F6' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#E4E0D6' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#FFFFFF' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#FFFFFF' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#D8D4C9' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#565D64' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#DCE4E2' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#0F6B62' }] },
];

/**
 * Asphalt — the brief's inverted set.
 *
 * Carried here and deliberately not wired up: the site has no dark surface and
 * no `prefers-color-scheme` handling, so switching to it today would put a dark
 * map in the middle of a paper page. It is transcribed rather than left in the
 * handoff folder because its three landmass greys are already design tokens —
 * `--sw-map-neighbour`, `--sw-map-land`, `--sw-map-land-edge` — so the palette
 * is in the product whether or not this map is, and the next person to build a
 * dark surface should find the matching basemap next to the light one rather
 * than rediscover the handoff.
 */
export const ASPHALT_MAP_STYLE: MapStyle = [
  { elementType: 'geometry', stylers: [{ color: '#14181B' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#9AA2A9' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#14181B' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#3A444B' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#1A1F23' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#242B30' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#14181B' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#2C343A' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#3A444B' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9AA2A9' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0E1113' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#0F6B62' }] },
];

export type { MapStyle };
